import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Grid, IconButton, InputAdornment, TextField, Toolbar } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { makeStyles } from '@mui/styles';
import React from 'react';
import moment from 'moment';

import { DataAPIContext, GET_LIST } from '../api';
import { debounce } from '../utils';

const useToolbarStyles = makeStyles((theme) => ({
    toolbarRoot: {
        marginBottom: 0,

    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
}));

export const TripChatChannelsToolbar = (props) => {
    const searchQueryRef = React.useRef(null);
    const classes = useToolbarStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const [tagOptions, setTagOptions] = React.useState([]);
    const [tagOptionsLoading, setTagOptionsLoading] = React.useState(false);

    React.useEffect(() => {
        if (!props.tripId) {
            return undefined;
        }
        let active = true;
        setTagOptionsLoading(true);
        dataAPI(GET_LIST, `trip_chat_list/${props.tripId}`, { urlSuffix: 'tags/' })
            .then((res) => active && setTagOptions(res.data || []))
            .catch(() => active && setTagOptions([]))
            .finally(() => active && setTagOptionsLoading(false));
        return () => {
            active = false;
        };
    }, [dataAPI, props.tripId]);

    const handleSearchQueryChange = (ev) => {
        debouncedSearchQueryChangeHandler.cancel();
        doSearch(searchQueryRef.current?.value);
    };

    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 300);

    const doSearch = (query) => props.onQueryChange && props.onQueryChange(query);

    const dateRange = props.dateRange || {};
    const dateRangePickerKey = `dr-${dateRange.start || ''}-${dateRange.end || ''}`;
    const dateRangePickerDefaultValue = React.useMemo(() => [
        dateRange.start ? moment(dateRange.start) : null,
        dateRange.end ? moment(dateRange.end) : null,
    ], [dateRange.start, dateRange.end]);

    const handleDateAccept = (acceptedDateRangeValue) => {
        const [newStart, newEnd] = acceptedDateRangeValue || [null, null];
        const start = newStart ? moment(newStart).format('YYYY-MM-DD') : null;
        const end = newEnd ? moment(newEnd).format('YYYY-MM-DD') : null;
        if (start !== (dateRange.start || null) || end !== (dateRange.end || null)) {
            props.setDateRange({ start, end });
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Toolbar disableGutters={true} className={classes.toolbarRoot}>
                <Grid container direction="row" spacing={2} wrap="wrap">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            variant="filled"
                            label="Search"
                            size="small"
                            onChange={debouncedSearchQueryChangeHandler}
                            inputRef={searchQueryRef}
                            defaultValue={props.channelListQuery}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton aria-label="do search" onClick={handleSearchQueryChange}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <DateRangePicker
                            key={dateRangePickerKey}
                            slots={{ field: SingleInputDateRangeField }}
                            defaultValue={dateRangePickerDefaultValue}
                            onAccept={handleDateAccept}
                            format="MMMM D, YYYY"
                            label="Travel Date"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    variant: 'filled',
                                    fullWidth: true,
                                },
                                actionBar: {
                                    actions: ['clear', 'accept'],
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Autocomplete
                            multiple
                            size="small"
                            limitTags={3}
                            options={tagOptions}
                            loading={tagOptionsLoading}
                            groupBy={(option) => option.category_name || ''}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={props.tagFilters || []}
                            onChange={(ev, nv) => props.setTagFilters(nv || [])}
                            noOptionsText="No tags on this trip's chats"
                            renderInput={(params) => (
                                <TextField {...params} variant="filled" label="Tags" />
                            )}
                        />
                    </Grid>
                </Grid>
            </Toolbar>
        </LocalizationProvider>
    );
};

export default TripChatChannelsToolbar;
